import * as THREE from 'three'
import { gsap } from 'gsap'

import Experience from "../Experience.js"
import Environment from './Environment.js'
import razor from './Razor.js'
import Floor from './Floor.js'
import Fireflies from './Fireflies.js'
import UiBloc from '../../Components/UI/UiBloc.js'

export default class World {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.resources = this.experience.resources
        this.debug = this.experience.debug
        this.uiBloc = new UiBloc()

        /**
         * Buttons
         */
        this.chromeCheckBox = this.uiBloc.chromeCheckBox
        this.roseGoldCheckBox = this.uiBloc.roseGoldCheckBox
        this.blackCheckBox = this.uiBloc.blackCheckBox

        this.chromeName = this.uiBloc.chromeName
        this.roseGoldName = this.uiBloc.roseGoldName
        this.blackName = this.uiBloc.blackName

        this.switch1 = this.uiBloc.switch1
        this.switch2 = this.uiBloc.switch2
        this.switchTheme = this.uiBloc.switchTheme

        //Debug
        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder('World').close()
        }
        this.parameters = {
            color: localStorage["theme-preference"] === 'light' ? 0xedeed7 : 0x1b1c10
        }

        if (this.debug.active) {
            this.debugFolder
                .addColor(this.parameters, 'color')
                .onChange(() => {
                    this.scene.background.set(this.parameters.color)
                })
        }

        this.lightBackground = new THREE.Color(0xedeed7)
        this.darkBackground = new THREE.Color(0x1b1c10)
        this.lightTarget = new THREE.Color(0xedeed7)
        this.darkTarget = new THREE.Color(0x1b1c10)

        this.lightBackgroundGLSL = new THREE.Vector3(0.929,0.933,0.843)
        this.darkBackgroundGLSL = new THREE.Vector3(0.106,0.11,0.063)
        this.lightBackgroundTargetGLSL = new THREE.Vector3(0.929,0.933,0.843)
        this.darkBackgroundTargetGLSL = new THREE.Vector3(0.106,0.11,0.063)

        this.lightColorOffGLSL = new THREE.Color(0xf2f2dd)
        this.darkColorOffGLSL = new THREE.Color(0x54533b)
        this.lightTargetGLSL = new THREE.Color(0xf2f2dd)
        this.darkTargetGLSL = new THREE.Color(0x54533b)


        //Color checkbox
        this.chromeCheckBox.addEventListener('click', this.chromecolor.bind(this))
        this.roseGoldCheckBox.addEventListener('click', this.roseGoldcolor.bind(this))
        this.blackCheckBox.addEventListener('click', this.blackcolor.bind(this))

        //Wait for resources
        this.resources.on('ready', () => {
            //Setup

            this.floor = new Floor()
            this.fireflies = new Fireflies()
            this.razor = new razor()
            this.environment = new Environment()

            this.pivotSwitch = gsap.fromTo(this.razor.base.rotation, { z: .8 }, { duration: 1.4, z: -.8, ease: "power2.inOut" })

            this.scene.background = localStorage["theme-preference"] === 'light' ? this.lightBackground : this.darkBackground
            // this.targetColor = localStorage["theme-preference"] === 'light' ? this.darkBackground : this.lightBackground
            this.floor.materialWood.map = localStorage["theme-preference"] === 'light' ? this.resources.items.baseColorTexture : this.resources.items.baseColorDarkTexture

            this.chromeMaterial = new THREE.MeshStandardMaterial({
                roughness: 0,
                metalness: 1,
                color: 0xeeeeee,
                envMap: this.environment.environmentMap.environment,
                envMapIntensity: this.environment.environmentMap.intensity
            })
            this.roseGoldMaterial = new THREE.MeshStandardMaterial({
                roughness: 0,
                metalness: 1,
                color: 0xff9696,
                envMap: this.environment.environmentMap.environment,
                envMapIntensity: localStorage["theme-preference"] === 'light' ? 1.6 : 1.7
            })

            this.blackMaterial = new THREE.MeshStandardMaterial({
                roughness: .3,
                metalness: 1,
                color: 0x444444,
                envMap: this.environment.environmentMap.environment,
                envMapIntensity: this.environment.environmentMap.intensity
            })

            this.chromecolor()

            this.rotateChoice = { value: -.3 }
            this.storedSpinState = true

            const mutationCallback = (entries) => {
                for (const mutation of entries) {

                    if (mutation.target.id === "switch-1") {
                        if (this.switch1.checked == true) {
                            gsap.to(this.rotateChoice, { duration: .8, value: -0.3, ease: "power2.inOut" })
                            this.storedSpinState = true
                        } else {
                            gsap.to(this.rotateChoice, { duration: .8, value: 0, ease: "power2.inOut" })
                            this.storedSpinState = false
                        }
                    }

                    if (mutation.target.id === "switch-2") {
                        this.playspring()
                    }

                    if (mutation.target.id === "theme-toggle") {
                        //theme color
                        this.updateTheme()

                    }

                }
            }
            const observer = new MutationObserver(mutationCallback)
            observer.observe(this.switch1, { attributes: true, attributeOldValue: true, attributeFilter: ["checked"] })
            observer.observe(this.switch2, { attributes: true, attributeOldValue: true, attributeFilter: ["checked"] })
            observer.observe(this.switchTheme, { attributes: true, attributeOldValue: true, attributeFilter: ["aria-label"] })

        })
    }

    chromecolor (event) {
        this.chromeCheckBox.checked = true
        this.roseGoldCheckBox.checked = false
        this.blackCheckBox.checked = false

        this.chromeName.style.display = "inline-flex"
        this.roseGoldName.style.display = "none"
        this.blackName.style.display = "none"
        this.razor.handle.material = this.chromeMaterial
        this.razor.icon.material = this.chromeMaterial
        this.razor.base.material = this.chromeMaterial
        this.razor.capot.material = this.chromeMaterial
        this.razor.screw.material = this.chromeMaterial
    }
    roseGoldcolor (event) {
        this.chromeCheckBox.checked = false
        this.roseGoldCheckBox.checked = true
        this.blackCheckBox.checked = false

        this.chromeName.style.display = "none"
        this.roseGoldName.style.display = "inline-block"
        this.blackName.style.display = "none"
        this.razor.handle.material = this.roseGoldMaterial
        this.razor.icon.material = this.roseGoldMaterial
        this.razor.base.material = this.roseGoldMaterial
        this.razor.capot.material = this.roseGoldMaterial
        this.razor.screw.material = this.roseGoldMaterial
    }
    blackcolor (event) {
        this.chromeCheckBox.checked = false
        this.roseGoldCheckBox.checked = false
        this.blackCheckBox.checked = true

        this.chromeName.style.display = "none"
        this.roseGoldName.style.display = "none"
        this.blackName.style.display = "inline-block"
        this.razor.handle.material = this.blackMaterial
        this.razor.icon.material = this.blackMaterial
        this.razor.base.material = this.blackMaterial
        this.razor.capot.material = this.blackMaterial
        this.razor.screw.material = this.blackMaterial
    }
    updateTheme () {
        if (localStorage["theme-preference"] === 'light') {
            gsap.to( this.floor.material1.uniforms.uBaseColor.value, {
                duration: .25,
                r: this.lightColorOffGLSL.r,
                g: this.lightColorOffGLSL.g,
                b: this.lightColorOffGLSL.b,
                ease: "power4.inOut",
                onComplete: () => {
                    this.darkColorOffGLSL = this.darkTargetGLSL
                    this.lightColorOffGLSL = this.lightTargetGLSL
                }
            })
            gsap.to(this.scene.background, {
                duration: .25,
                r: this.lightBackground.r,
                g: this.lightBackground.g,
                b: this.lightBackground.b,
                ease: "power4.inOut",
                onComplete: () => {
                    this.darkBackground = this.darkTarget
                    this.lightBackground = this.lightTarget
                }
            })

            
            window.setTimeout(() => {

                this.floor.materialWood.map = this.resources.items.baseColorTexture

                gsap.to(this.chromeMaterial, { duration: .25, envMapIntensity: 1.6 })
                this.chromeMaterial.envMap = this.environment.environmentMap.textureLight
                gsap.to(this.roseGoldMaterial, { duration: .25, envMapIntensity: 1.6 })
                this.roseGoldMaterial.envMap = this.environment.environmentMap.textureLight
                gsap.to(this.blackMaterial, { duration: .25, envMapIntensity: 1.6 })
                this.blackMaterial.envMap = this.environment.environmentMap.textureLight

                this.environment.setEnvironmentMap()
            }, 100)
            // gsap.to(this.floor.material.uniforms.uStrength, { duration: 1.5, value: 1, ease: 'power2.in' })


        } else {
            gsap.to(this.scene.background, {
                duration: .25,
                r: this.darkBackground.r,
                g: this.darkBackground.g,
                b: this.darkBackground.b,
                ease: "power4.inOut",
                onComplete: () => {
                    this.darkBackground = this.darkTarget
                    this.lightBackground = this.lightTarget
                }
            })
            gsap.to( this.floor.material1.uniforms.uBaseColor.value, {
                duration: .25,
                r: this.darkColorOffGLSL.r,
                g: this.darkColorOffGLSL.g,
                b: this.darkColorOffGLSL.b,
                ease: "power4.inOut",
                onComplete: () => {
                    this.darkColorOffGLSL = this.darkTargetGLSL
                    this.lightColorOffGLSL = this.lightTargetGLSL
                }
            })

            window.setTimeout(() => {
                this.floor.materialWood.map = this.resources.items.baseColorDarkTexture
                
                gsap.to(this.chromeMaterial, { duration: .25, envMapIntensity: 1.5 })
                this.chromeMaterial.envMap = this.environment.environmentMap.textureDark
                gsap.to(this.roseGoldMaterial, { duration: .25, envMapIntensity: 1.7 })
                this.roseGoldMaterial.envMap = this.environment.environmentMap.textureDark
                gsap.to(this.blackMaterial, { duration: .25, envMapIntensity: 1.4 })
                this.blackMaterial.envMap = this.environment.environmentMap.textureDark

                this.environment.setEnvironmentMap()
            }, 100)

            // gsap.to(this.floor.material.uniforms.uStrength, { duration: 1.5, value: 0, ease: 'power2.in' })

        }

    }

    playspring () {
        if (this.switch2.checked == true) {

            if (this.switch1.checked == true) {
                this.storedSpinState = true
                this.switch1.checked = false
                gsap.to(this.rotateChoice, { duration: .8, value: 0, ease: "power2.inOut" })
            } else {
                this.storedSpinState = false
            }

            if (this.razor.handle.rotation.y < -Math.PI) {
                gsap.to(this.razor.handle.rotation, { duration: .8, y: -(Math.PI * 2), ease: "power2.inOut" })
            } else {
                gsap.to(this.razor.handle.rotation, { duration: .8, y: 0, ease: "power2.inOut" })
            }

            // this.experience.camera.controls.setLookAt(4, 2, 0, -0.5, 0, 0, true)
            gsap.to(this.experience.camera.instance.position, {
                duration: 1.5,
                x: -4,
                y: 2.3,
                z: 0,
                ease: "power2.out"
            })
            gsap.to(this.experience.camera.targetPosition, {
                duration: 1.5,
                y: 1,
                ease: "power2.out"
            })

            window.setTimeout(() => {
                this.pivotSwitch.pause()
                this.pivotSwitch.reverse()


            }, 500)


        } else {
            if (this.storedSpinState === true) {
                gsap.to(this.rotateChoice, { duration: .8, value: -0.3, ease: "power2.inOut" })
                this.switch1.checked = "true"

            }
            this.pivotSwitch.pause()
            this.pivotSwitch.play()


            window.setTimeout(() => {
                // this.experience.camera.controls.setLookAt(2, 2, -5, 0, 0, 0, true)
                gsap.to(this.experience.camera.instance.position, {
                    duration: 2,
                    x: -5.5,
                    y: 1,
                    z: 0,
                    ease: "power2.out"
                })
                gsap.to(this.experience.camera.targetPosition, {
                    duration: 2,
                    y: .5,
                    ease: "power2.out",
                })
            }, 100)
        }
    }
    //Recycle blades
    playblade () {
        // text animation
        if(this.floor.material1.uniforms.uColor)
            gsap.to( this.floor.material1.uniforms.uColor, { duration: 1, value: 0, ease: "power2.inOut" })
        if(this.floor.material2.uniforms.uColor)
            gsap.to( this.floor.material2.uniforms.uColor, { duration: 1, value: 0, ease: "power2.inOut" })
        if(this.floor.material2.uniforms.uColor)
            gsap.to( this.floor.material3.uniforms.uColor, { duration: 1, value: 0, ease: "power2.inOut" })

        //razor animation
        if (this.razor.animation.action !== null) {

            if (this.switch1.checked == true) {
                this.switch1.checked = false
                gsap.to(this.rotateChoice, { duration: .8, value: 0, ease: "power2.inOut" })
            }
            if (this.switch2.checked == true) {
                this.switch2.checked = false
                gsap.to(this.razor.base.rotation, { duration: 1, z: -1.0238002644524968, ease: "power2.inOut" })
            }
            this.switch1.setAttribute("disabled", "")
            this.switch1.parentElement.setAttribute("disabled", "")
            this.switch2.setAttribute("disabled", "")
            this.switch2.parentElement.setAttribute("disabled", "")

            if (this.razor.handle.rotation.y < -Math.PI) {
                gsap.to(this.razor.handle.rotation, { duration: 1, y: -(Math.PI * 2), ease: "power2.inOut" })
            } else {
                gsap.to(this.razor.handle.rotation, { duration: 1, y: 0, ease: "power2.inOut" })
            }

            gsap.to(this.razor.handle.position, { duration: 1, y: 0.6107150316238403, ease: "power2.inOut" })

            // this.experience.camera.controls.setLookAt(-3, 3.5, 2.5, 0, 0, 0, true)

            gsap.to(this.experience.camera.instance.position, {
                duration: 2,
                x: -2,
                y: 2.7,
                z: 2.5,
                ease: "power2.out"
            })
            gsap.to(this.experience.camera.targetPosition, {
                duration: 2,
                y: 1.7,
                ease: "power2.out",
            })
            window.setTimeout(() => {
                this.razor.animation.action.play()
            }, 1000)

            window.setTimeout(() => {
                //this.experience.camera.controls.setLookAt(-6, 3.2, 6.5, 0, -3.5, 0, true)

                gsap.to(this.experience.camera.instance.position, {
                    duration: 2,
                    x: -3,
                    y: 3,
                    z: 3,
                })
                gsap.to(this.experience.camera.targetPosition, {
                    duration: 2,
                    y: 1.2,
                    ease: "power2.out",
                })
            }, 9200)
        }
    }
    //Load 3 blades
    loadall (event) {

        //text animation
        gsap.to( this.floor.material2.uniforms.uColor, { duration: 1, value: 1, ease: "power2.inOut" })

        
        if (this.razor.animation.action !== null) {
            if (this.razor.blade2.visible === false) {
                this.razor.blade2.visible = true
            }
            if (this.razor.blade1.visible === false) {
                this.razor.blade1.visible = true
            }
            if (this.razor.blade0.visible === false) {
                this.razor.blade0.visible = true
            }



            gsap.to(this.razor.blade0.position, { duration: .1, z: .45, ease: "power2.inOut" })
            gsap.to(this.razor.blade0.scale, { delay: .1, duration: 1, x: 1, y: 1, z: 1, ease: "power2.inOut" })
            gsap.to(this.razor.blade0.position, { delay: .1, duration: 1, x: -0.030611591413617134, y: .05, z: 0.0005263719940558076, ease: "power2.inOut" })
            gsap.to(this.razor.blade0.rotation, { delay: .1, duration: 1, x: -Math.PI, ease: "power2.inOut" })
            gsap.to(this.razor.blade0.position, { delay: 1.1, duration: .6, y: 0.02815, ease: "power2.inOut" })

            gsap.to(this.razor.support1.rotation, { delay: 1.4, duration: 1, z: "-=2.321288", ease: "power4.inOut" })

            gsap.to(this.razor.blade1.position, { delay: 1.7, duration: .1, z: .45, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.rotation, { delay: 1.7, duration: .1, x: -Math.PI, y: Math.PI, z: "-=3.8", ease: "power2.inOut" })
            gsap.to(this.razor.blade1.scale, { delay: 1.8, duration: 1, x: 1, y: 1, z: 1, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.position, { delay: 1.8, duration: 1, x: .07, y: -.01, z: 0.0010502373334020376, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.rotation, { delay: 1.8, duration: 1, x: -Math.PI, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.position, { delay: 2.8, duration: .6, x: 0.04844268783926964, y: -0.025725534185767174, ease: "power2.inOut" })

            gsap.to(this.razor.support2.rotation, { delay: 3.1, duration: 1, z: "-=2.321288", ease: "power4.inOut" })

            gsap.to(this.razor.blade2.position, { delay: 3.5, duration: .1, z: .45, ease: "power2.inOut" })
            gsap.to(this.razor.blade2.rotation, { delay: 3.5, duration: .1, x: -Math.PI, y: Math.PI, z: "-=3.8", ease: "power2.inOut" })
            gsap.to(this.razor.blade2.scale, { delay: 3.6, duration: 1, x: 1, y: 1, z: 1, ease: "power2.inOut" })
            gsap.to(this.razor.blade2.position, { delay: 3.6, duration: 1, x: .06, y: 0, z: 0.0010502373334020376, ease: "power2.inOut" })
            gsap.to(this.razor.blade2.rotation, { delay: 3.6, duration: 1, x: -Math.PI, ease: "power2.inOut" })
            gsap.to(this.razor.blade2.position, { delay: 4.6, duration: .6, x: 0.0462222695350647, y: -0.008819570764899254, ease: "power2.inOut" })

            gsap.to(this.razor.capot.rotation, { delay: 4.9, duration: 1, z: "-=2.321288", ease: "power4.inOut" })

            gsap.to(this.razor.handle.rotation, { delay: 5.4, duration: 1.5, y: Math.PI, ease: "power2.inOut" })
            gsap.to(this.razor.handle.position, { delay: 5.4, duration: 1.5, x: -0.03524313122034073, ease: "power2.inOut" })

            gsap.to(this.razor.screw.position, { delay: 6.6, duration: .5, y: .01, ease: "power2.inOut" })
            gsap.to(this.razor.screw.rotation, { delay: 7, duration: 2, y: "+=18.7", ease: "power4.inOut" })
            gsap.to(this.razor.screw.position, { delay: 7, duration: 2, y: .015, ease: "power2.inOut" })

            window.setTimeout(() => {
                //this.experience.camera.controls.setLookAt(-3, 3.5, 2.5, 0, 0, 0, true)

                gsap.to(this.experience.camera.instance.position, {
                    duration: 2,
                    x: -3,
                    y: 3.5,
                    z: 2.5,
                    ease: "power2.out"
                })
                gsap.to(this.experience.camera.targetPosition, {
                    duration: 2,
                    y: 1.5,
                    ease: "power2.out",
                })
            }, 100)

            window.setTimeout(() => {
                if (this.storedSpinState == true) {
                    gsap.to(this.rotateChoice, { duration: .8, value: -0.3, ease: "power2.inOut" })
                    this.switch1.checked = "true"
                }
                this.razor.animation.action.stop()
                this.razor.screw.position.y = 0.001

                //this.experience.camera.controls.setLookAt(2, 2, -5, 0, 0, 0, true)

                gsap.to(this.experience.camera.instance.position, {
                    duration: 2,
                    x: -5.5,
                    y: 1,
                    z: 0,
                    ease: "power2.out"
                })
                gsap.to(this.experience.camera.targetPosition, {
                    duration: 2,
                    y: .5,
                    ease: "power2.out",
                })
                this.switch1.removeAttribute("disabled", "")
                this.switch2.removeAttribute("disabled", "")
                this.switch1.parentElement.removeAttribute("disabled", "")
                this.switch2.parentElement.removeAttribute("disabled", "")
            }, 9100)
        }
    }
    //Load 2 blades Top
    loadtop (event) {
         //text animation
         gsap.to( this.floor.material1.uniforms.uColor, { duration: 1, value: 1, ease: "power2.inOut" })

        if (this.razor.animation.action !== null) {

            if (this.razor.blade2.visible === false) {
                this.razor.blade2.visible = true
            }
            if (this.razor.blade1.visible === false) {
                this.razor.blade1.visible = true
            }



            gsap.to(this.razor.support1.rotation, { delay: 0, duration: 1, z: "-=2.321288", ease: "power4.inOut" })

            gsap.to(this.razor.blade1.position, { delay: .3, duration: .1, z: .45, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.rotation, { delay: .3, duration: .1, x: -Math.PI, y: Math.PI, z: "-=3.8", ease: "power2.inOut" })
            gsap.to(this.razor.blade1.scale, { delay: .4, duration: 1, x: 1, y: 1, z: 1, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.position, { delay: .4, duration: 1, x: .07, y: -.01, z: 0.0010502373334020376, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.rotation, { delay: .4, duration: 1, x: -Math.PI, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.position, { delay: 1.4, duration: .6, x: 0.04844268783926964, y: -0.025725534185767174, ease: "power2.inOut" })

            gsap.to(this.razor.support2.rotation, { delay: 1.7, duration: 1, z: "-=2.321288", ease: "power4.inOut" })

            gsap.to(this.razor.blade2.position, { delay: 2.1, duration: .1, z: .45, ease: "power2.inOut" })
            gsap.to(this.razor.blade2.rotation, { delay: 2.1, duration: .1, x: -Math.PI, y: Math.PI, z: "-=3.8", ease: "power2.inOut" })
            gsap.to(this.razor.blade2.scale, { delay: 2.2, duration: 1, x: 1, y: 1, z: 1, ease: "power2.inOut" })
            gsap.to(this.razor.blade2.position, { delay: 2.2, duration: 1, x: .06, y: 0, z: 0.0010502373334020376, ease: "power2.inOut" })
            gsap.to(this.razor.blade2.rotation, { delay: 2.2, duration: 1, x: -Math.PI, ease: "power2.inOut" })
            gsap.to(this.razor.blade2.position, { delay: 3.2, duration: .6, x: 0.0462222695350647, y: -0.008819570764899254, ease: "power2.inOut" })

            gsap.to(this.razor.capot.rotation, { delay: 3.5, duration: 1, z: "-=2.321288", ease: "power4.inOut" })

            gsap.to(this.razor.handle.rotation, { delay: 4, duration: 1.5, y: Math.PI, ease: "power2.inOut" })
            gsap.to(this.razor.handle.position, { delay: 4, duration: 1.5, x: -0.03524313122034073, ease: "power2.inOut" })

            gsap.to(this.razor.screw.position, { delay: 5.2, duration: .5, y: .01, ease: "power2.inOut" })
            gsap.to(this.razor.screw.rotation, { delay: 5.6, duration: 2, y: "+=18.7", ease: "power4.inOut" })
            gsap.to(this.razor.screw.position, { delay: 5.6, duration: 2, y: .015, ease: "power2.inOut" })

            window.setTimeout(() => {
                //this.experience.camera.controls.setLookAt(-3, 3.5, 2.5, 0, 0, 0, true)
                gsap.to(this.experience.camera.instance.position, {
                    duration: 2,
                    x: -3,
                    y: 3.5,
                    z: 2.5,
                    ease: "power2.out"
                })
                gsap.to(this.experience.camera.targetPosition, {
                    duration: 2,
                    y: 1.5,
                    ease: "power2.out",
                })
            }, 100)

            window.setTimeout(() => {
                if (this.storedSpinState == true) {
                    gsap.to(this.rotateChoice, { duration: .8, value: -0.3, ease: "power2.inOut" })
                    this.switch1.checked = "true"
                }
                this.razor.animation.action.stop()
                this.razor.screw.position.y = 0.001
                this.razor.blade0.visible = false


                //this.experience.camera.controls.setLookAt(2, 2, -5, 0, 0, 0, true)

                gsap.to(this.experience.camera.instance.position, {
                    duration: 2,
                    x: -5.5,
                    y: 1,
                    z: 0,
                    ease: "power2.out"
                })
                gsap.to(this.experience.camera.targetPosition, {
                    duration: 2,
                    y: .5,
                    ease: "power2.out",
                })
                this.switch1.removeAttribute("disabled", "")
                this.switch2.removeAttribute("disabled", "")
                this.switch1.parentElement.removeAttribute("disabled", "")
                this.switch2.parentElement.removeAttribute("disabled", "")
            }, 7700)
        }
    }
    //Load 2 blades Bottom
    loadbottom (event) {
          //text animation
          gsap.to( this.floor.material3.uniforms.uColor, { duration: 1, value: 1, ease: "power2.inOut" })

        if (this.razor.animation.action !== null) {


            if (this.razor.blade1.visible == false) {
                this.razor.blade1.visible = true
            }
            if (this.razor.blade0.visible == false) {
                this.razor.blade0.visible = true
            }

            gsap.to(this.razor.blade0.position, { duration: .1, z: .45, ease: "power2.inOut" })
            gsap.to(this.razor.blade0.scale, { delay: .1, duration: 1, x: 1, y: 1, z: 1, ease: "power2.inOut" })
            gsap.to(this.razor.blade0.position, { delay: .1, duration: 1, x: -0.030611591413617134, y: .05, z: 0.0005263719940558076, ease: "power2.inOut" })
            gsap.to(this.razor.blade0.rotation, { delay: .1, duration: 1, x: -Math.PI, ease: "power2.inOut" })
            gsap.to(this.razor.blade0.position, { delay: 1.1, duration: .6, y: 0.02815, ease: "power2.inOut" })

            gsap.to(this.razor.support1.rotation, { delay: 1.4, duration: 1, z: "-=2.321288", ease: "power4.inOut" })

            gsap.to(this.razor.blade1.position, { delay: 1.7, duration: .1, z: .45, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.rotation, { delay: 1.7, duration: .1, x: -Math.PI, y: Math.PI, z: "-=3.8", ease: "power2.inOut" })
            gsap.to(this.razor.blade1.scale, { delay: 1.8, duration: 1, x: 1, y: 1, z: 1, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.position, { delay: 1.8, duration: 1, x: .07, y: -.01, z: 0.0010502373334020376, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.rotation, { delay: 1.8, duration: 1, x: -Math.PI, ease: "power2.inOut" })
            gsap.to(this.razor.blade1.position, { delay: 2.8, duration: .6, x: 0.04844268783926964, y: -0.025725534185767174, ease: "power2.inOut" })

            gsap.to(this.razor.support2.rotation, { delay: 3.1, duration: 1, z: "-=2.321288", ease: "power4.inOut" })

            gsap.to(this.razor.capot.rotation, { delay: 3.5, duration: 1, z: "-=2.321288", ease: "power4.inOut" })

            gsap.to(this.razor.handle.rotation, { delay: 4, duration: 1.5, y: Math.PI, ease: "power2.inOut" })
            gsap.to(this.razor.handle.position, { delay: 4, duration: 1.5, x: -0.03524313122034073, ease: "power2.inOut" })

            gsap.to(this.razor.screw.position, { delay: 5.2, duration: .5, y: .01, ease: "power2.inOut" })
            gsap.to(this.razor.screw.rotation, { delay: 5.6, duration: 2, y: "+=18.7", ease: "power4.inOut" })
            gsap.to(this.razor.screw.position, { delay: 5.6, duration: 2, y: .015, ease: "power2.inOut" })

            window.setTimeout(() => {
                // this.experience.camera.controls.setLookAt(-3, 3.5, 2.5, 0, 0, 0, true)

                gsap.to(this.experience.camera.instance.position, {
                    duration: 2,
                    x: -3,
                    y: 3.5,
                    z: 2.5,
                    ease: "power2.out"
                })
                gsap.to(this.experience.camera.targetPosition, {
                    duration: 2,
                    y: 1.5,
                    ease: "power2.out",
                })
            }, 100)

            window.setTimeout(() => {
                if (this.storedSpinState == true) {
                    gsap.to(this.rotateChoice, { duration: .8, value: -0.3, ease: "power2.inOut" })
                    this.switch1.checked = "true"
                }
                this.razor.animation.action.stop()
                this.razor.screw.position.y = 0.001
                this.razor.blade2.visible = false

                // this.experience.camera.controls.setLookAt(2, 2, -5, 0, 0, 0, true)

                gsap.to(this.experience.camera.instance.position, {
                    duration: 2,
                    x: -5.5,
                    y: 1,
                    z: 0,
                    ease: "power2.out"
                })
                gsap.to(this.experience.camera.targetPosition, {
                    duration: 2,
                    y: .5,
                    ease: "power2.out",
                })
                this.switch1.removeAttribute("disabled", "")
                this.switch2.removeAttribute("disabled", "")
                this.switch1.parentElement.removeAttribute("disabled", "")
                this.switch2.parentElement.removeAttribute("disabled", "")
            }, 7700)
        }
    }

    update () {
        if (this.razor) {
            this.razor.update()
        }
        if (this.fireflies) {
            this.fireflies.update()
        }
        if (this.floor) {
            this.floor.update()
        }
        this.uiBloc.update()
    }
}